import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { FC } from "react";
import { formatAmount } from "src/tools/tools";
import { PayswixLogo } from "./StatementPDF";
import "./fonts";

export interface IStatementsListTotals {
	total_net?: number;
	total_fees?: number;
	total_amount?: number;
	total_failed?: number;
	total_refunds?: number;
	total_chargebacks?: number;
	total_oct?: number;
	total_rollings?: number;
	total_ajustment_in?: number;
	total_ajustment_out?: number;
	total_release_rollings?: number;
}

export interface IStatementsListPdfStatement {
	id: number;
	status: string;
	period_start: string | null;
	period_end: string | null;
	totals?: Record<string, IStatementsListTotals> | null;
}

export interface IStatementsListPdfClient {
	clientId: number;
	clientName: string;
	registrationNumber?: string | number | null;
	clientStatus?: string | null;
	statements: IStatementsListPdfStatement[];
}

export interface StatementsListPDFProps {
	clients: IStatementsListPdfClient[];
	generatedAt?: Date;
	isPayswix?: boolean;
}

const normalizeUtcDateTime = (value?: string | Date | null) => {
	if (!value) return "";
	const normalized = String(value).trim().replace("T", " ");
	return normalized.replace(/\.\d+Z?$/, "").replace(/Z$/, "").replace(/[+-]\d{2}:?\d{2}$/, "").trim();
};

const parseUtcDate = (value?: string | Date | null) => {
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

	const normalized = normalizeUtcDateTime(value);
	if (!normalized) return null;

	const isoValue = normalized.includes(" ") ? normalized.replace(" ", "T") : normalized;
	const date = new Date(`${isoValue}${isoValue.includes("T") ? "Z" : "T00:00:00Z"}`);
	return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (value?: string | Date | null) => {
	const date = parseUtcDate(value);
	return date
		? new Intl.DateTimeFormat(undefined, { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(date)
		: "-";
};

const formatPeriod = (start?: string | null, end?: string | null) => {
	if (!start && !end) return "-";
	return `${formatDate(start)} - ${formatDate(end)}`;
};

const TOTAL_COLS: { label: string; key: keyof IStatementsListTotals | "ccy" }[] = [
	{ get label() { return msg("ccy"); }, key: "ccy" },
	{ get label() { return msg("turnover"); }, key: "total_amount" },
	{ get label() { return msg("adjIn"); }, key: "total_ajustment_in" },
	{ get label() { return msg("adjOut"); }, key: "total_ajustment_out" },
	{ get label() { return msg("refunds"); }, key: "total_refunds" },
	{ get label() { return msg("chargebacks"); }, key: "total_chargebacks" },
	{ get label() { return msg("oct"); }, key: "total_oct" },
	{ get label() { return msg("failed"); }, key: "total_failed" },
	{ get label() { return msg("rollings"); }, key: "total_rollings" },
	{ get label() { return msg("released"); }, key: "total_release_rollings" },
	{ get label() { return msg("fees"); }, key: "total_fees" },
	{ get label() { return msg("net_e2ffa4f1"); }, key: "total_net" },
];

const TotalsTable: FC<{ totals?: Record<string, IStatementsListTotals> | null }> = ({ totals }) => {
	useLocale();
	const entries = totals ? Object.entries(totals) : [];

	if (entries.length === 0) {
		return <Text style={s.emptyText}>{msg("noTotals")}</Text>;
	}

	return (
		<View style={s.table}>
			<View style={[s.tableRow, s.tableHeaderRow]}>
				{TOTAL_COLS.map((col, index) => (
					<Text key={col.key} style={[s.tableCell, s.tableCellTop, index === TOTAL_COLS.length - 1 ? s.tableCellLast : {}, s.tableHeaderCell, col.key === "ccy" ? s.ccyCell : s.numCell]}>
						{col.label}
					</Text>
				))}
			</View>
			{entries.map(([currency, totalsRow], rowIndex) => (
				<View key={currency} style={[s.tableRow, rowIndex % 2 === 0 ? s.tableRowAlt : {}]}>
					{TOTAL_COLS.map((col, index) => (
						<Text
							key={col.key}
							style={[s.tableCell, index === TOTAL_COLS.length - 1 ? s.tableCellLast : {}, col.key === "ccy" ? s.ccyCell : s.numCell, col.key === "total_net" ? s.boldCell : {}]}>
							{col.key === "ccy" ? currency : formatAmount(totalsRow[col.key] ?? 0)}
						</Text>
					))}
				</View>
			))}
		</View>
	);
};

const PayswixFooter = () => { useLocale(); return ((
	<View style={s.payswixFooterContent}>
		<View style={s.footerLogo}>
			<PayswixLogo width={50} height={12} />
		</View>
		<View style={s.payswixFooterInfo}>
			<Text style={s.payswixFooterText}>UAB Payswix, Spaudos str. 7, 05132, Vilnius, Lithuania.</Text>
			<Text style={s.payswixFooterText}>Company No: 304604766; VAT No: LT100015296012 </Text>
		</View>
	</View>
)); };

export const StatementsListPDF: FC<StatementsListPDFProps> = ({ clients, generatedAt = new Date(), isPayswix }) => { useLocale(); return ((
	<Document>
		<Page size="A4" orientation="portrait" style={s.page}>
			<View style={s.docHeader} fixed>
				{isPayswix ? (
					<View style={s.headerLogo}>
						<PayswixLogo width={148} height={30} />
					</View>
				) : (
					<Text style={s.docDate}>{msg("generated")}{formatDate(generatedAt)}</Text>
				)}
			</View>
			<Text style={s.docTitle}>{msg("statements")}</Text>

			{clients.map(client => (
				<View key={client.clientId} style={s.clientBlock}>
					<View style={s.clientHeader}>
						<View>
							<Text style={s.clientName}>{client.clientName}</Text>
							<Text style={s.clientMeta}>{msg("clientId")}{client.clientId}</Text>
							<Text style={s.clientMeta}>{msg("registrationNumber_3610cdff")}{client.registrationNumber || "-"}</Text>
						</View>
						{client.clientStatus && <Text style={s.clientStatus}>{client.clientStatus}</Text>}
					</View>

					{client.statements.map(statement => (
						<View key={statement.id} style={s.statementBlock} wrap={false}>
							<View style={s.statementHeader}>
								<Text style={s.statementTitle}>{msg("statement_c3ef99a2")}{statement.id}</Text>
								<Text style={s.statementMeta}>{msg("period_8ac7d1d6")}{formatPeriod(statement.period_start, statement.period_end)}</Text>
								<Text style={s.statementStatus}>{msg("status_4889951f")}{statement.status}</Text>
							</View>
							<TotalsTable totals={statement.totals} />
						</View>
					))}
				</View>
			))}

			<View style={s.footer} fixed>
				{isPayswix ? <PayswixFooter /> : <Text style={s.footerText}>{msg("statements_2e3b9edf")}{clients.reduce((sum, client) => sum + client.statements.length, 0)}</Text>}
				<Text style={s.footerText} render={({ pageNumber, totalPages }) => msg("templatePage_1bcd3a91", { v0: pageNumber, v1: totalPages })} />
			</View>
		</Page>
	</Document>
)); };

const s = StyleSheet.create({
	page: {
		paddingTop: 24,
		paddingHorizontal: 42,
		paddingBottom: 62,
		fontFamily: "Euclid",
		fontSize: 8,
		color: "#111111",
	},
	docHeader: {
		flexDirection: "row",
		justifyContent: "flex-end",
		alignItems: "flex-start",
		minHeight: 54,
		marginBottom: 12,
	},
	docTitle: { fontSize: 15, fontWeight: 400, color: "#111111", marginBottom: 12 },
	docDate: { fontSize: 8, color: "#555555", marginTop: 2 },
	headerLogo: { width: 148, height: 30 },
	clientBlock: {
		marginBottom: 16,
	},
	clientHeader: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		borderBottom: "0.6pt solid #111111",
		paddingBottom: 5,
		marginBottom: 8,
	},
	clientName: { fontSize: 9.5, fontWeight: 700, color: "#111111" },
	clientMeta: { marginTop: 2, fontSize: 7.2, color: "#333333" },
	clientStatus: {
		fontSize: 7,
		fontWeight: 700,
		textTransform: "uppercase",
		color: "#111111",
	},
	statementBlock: {
		marginBottom: 10,
	},
	statementHeader: {
		flexDirection: "row",
		alignItems: "center",
		gap: 12,
		marginBottom: 5,
	},
	statementTitle: { fontSize: 8.5, fontWeight: 700, color: "#111111" },
	statementMeta: { fontSize: 7.5, color: "#333333" },
	statementStatus: {
		fontSize: 7.5,
		fontWeight: 700,
		textTransform: "uppercase",
		color: "#111111",
	},
	table: { flexDirection: "column" },
	tableRow: { flexDirection: "row" },
	tableRowAlt: { backgroundColor: "#ffffff" },
	tableHeaderRow: { backgroundColor: "#f2f2f2" },
	tableCell: {
		flex: 1,
		padding: "4pt 3pt",
		fontSize: 5.8,
		borderBottom: "0.5pt solid #d9d9d9",
		borderLeft: "0.5pt solid #d9d9d9",
	},
	tableCellTop: {
		borderTop: "0.5pt solid #d9d9d9",
	},
	tableCellLast: {
		borderRight: "0.5pt solid #d9d9d9",
	},
	tableHeaderCell: {
		fontSize: 5.4,
		fontWeight: 700,
		color: "#111111",
		textTransform: "uppercase",
	},
	ccyCell: { flex: 0, minWidth: 24, maxWidth: 32 },
	numCell: { textAlign: "right" },
	boldCell: { fontWeight: 700 },
	emptyText: {
		fontSize: 7.5,
		color: "#555555",
		padding: "4pt 0",
	},
	footer: {
		position: "absolute",
		bottom: 18,
		left: 42,
		right: 42,
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "flex-end",
		borderTop: "0.6pt solid #111111",
		paddingTop: 7,
	},
	footerText: { fontSize: 7, color: "#111111" },
	payswixFooterContent: {
		flexDirection: "row",
		alignItems: "center",
		width: 420,
	},
	footerLogo: { width: 50, height: 12, marginRight: 34 },
	payswixFooterInfo: {
		flexDirection: "column",
	},
	payswixFooterText: { fontSize: 4.8, color: "#111111" },
});
